import { useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  ForecastCategoryBreakdownRow,
  ForecastProjection,
  ForecastProjectionPoint,
} from "../../hooks/useForecastProjection";
import type { Currency } from "../../types";
import { formatYmMonthShort } from "../../utils/formatYmMonthShort";
import {
  CATEGORY_COLOR_SWATCHES,
  DEFAULT_CATEGORY_COLOR,
} from "../categories/categoryColors";
import { formatK } from "../cashflow/CashflowContext";
import { PROJECTION_SCENARIO_SELECT_OPTIONS } from "../../constants/projectionScenarioSelect";

const FLOW_OTHER_ID = "__forecast_other__";
/** Max distinct categories per stack (inflows / outflows); remainder rolls into Other. */
const MAX_CATEGORY_BAR_SEGMENTS = 8;

type Props = {
  data: ForecastProjection | null;
  isLoading: boolean;
  scenario: "bad" | "mid" | "good";
  onScenarioChange: (value: "bad" | "mid" | "good") => void;
  currency: Currency;
  /** When set, bar segment colors match category settings (same as forecast rules table). */
  categoryColorById?: Readonly<Record<string, string>>;
  /** Category id -> parent category id (null/undefined when already top-level). */
  categoryParentById?: Readonly<Record<string, string | null | undefined>>;
  /** Category id -> canonical category label. */
  categoryLabelById?: Readonly<Record<string, string>>;
};

type FlowSeriesMeta = {
  dataKey: string;
  displayName: string;
  color: string;
};

function incomeBarKey(categoryId: string): string {
  return `in_${categoryId}`;
}

function expenseBarKey(categoryId: string): string {
  return `out_${categoryId}`;
}

function fallbackColorForCategoryId(categoryId: string): string {
  const swatches = CATEGORY_COLOR_SWATCHES;
  let h = 0;
  for (let i = 0; i < categoryId.length; i++) {
    h = (h * 31 + categoryId.charCodeAt(i)) >>> 0;
  }
  return swatches[h % swatches.length] ?? DEFAULT_CATEGORY_COLOR;
}

function resolveCategoryBarColor(
  categoryId: string,
  categoryColorById?: Readonly<Record<string, string>>,
): string {
  const custom = categoryColorById?.[categoryId]?.trim();
  if (custom) return custom;
  return fallbackColorForCategoryId(categoryId);
}

function hasAnyCategoryBreakdown(points: ForecastProjectionPoint[]): boolean {
  return points.some((p) => (p.categoryBreakdown?.length ?? 0) > 0);
}

function foldBreakdownRowsToParents(
  rows: ForecastCategoryBreakdownRow[] | undefined,
  categoryParentById?: Readonly<Record<string, string | null | undefined>>,
  categoryLabelById?: Readonly<Record<string, string>>,
): ForecastCategoryBreakdownRow[] {
  if (!rows?.length) return [];
  if (!categoryParentById) return rows;

  const merged = new Map<string, ForecastCategoryBreakdownRow>();
  for (const row of rows) {
    const parentId = categoryParentById[row.categoryId];
    const rolledId = parentId || row.categoryId;
    const key = `${row.categoryType}:${rolledId}`;
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, {
        categoryId: rolledId,
        categoryType: row.categoryType,
        categoryName: categoryLabelById?.[rolledId] || row.categoryName,
        amount: row.amount,
      });
      continue;
    }
    existing.amount += row.amount;
    existing.categoryName = categoryLabelById?.[rolledId] || existing.categoryName;
  }
  return Array.from(merged.values());
}

function normalizePointsToParentBreakdown(
  points: ForecastProjectionPoint[],
  categoryParentById?: Readonly<Record<string, string | null | undefined>>,
  categoryLabelById?: Readonly<Record<string, string>>,
): ForecastProjectionPoint[] {
  if (!categoryParentById) return points;
  return points.map((p) => ({
    ...p,
    categoryBreakdown: foldBreakdownRowsToParents(
      p.categoryBreakdown,
      categoryParentById,
      categoryLabelById,
    ),
  }));
}

function buildCategoryBarChartModel(
  points: ForecastProjectionPoint[],
  maxSegmentsPerStack: number,
  getSegmentColor: (categoryId: string) => string,
): {
  rows: Record<string, string | number>[];
  incomeBars: FlowSeriesMeta[];
  expenseBars: FlowSeriesMeta[];
} {
  const incomeScores = new Map<string, number>();
  const expenseScores = new Map<string, number>();
  const incomeNames = new Map<string, string>();
  const expenseNames = new Map<string, string>();

  for (const p of points) {
    for (const row of p.categoryBreakdown ?? []) {
      const id = row.categoryId;
      const mag = row.amount;
      if (row.categoryType === "expense") {
        expenseScores.set(id, (expenseScores.get(id) ?? 0) + mag);
        expenseNames.set(id, row.categoryName);
      } else {
        incomeScores.set(id, (incomeScores.get(id) ?? 0) + mag);
        incomeNames.set(id, row.categoryName);
      }
    }
  }

  const sortIds = (scores: Map<string, number>) =>
    [...scores.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([id]) => id);

  const incomeSorted = sortIds(incomeScores);
  const expenseSorted = sortIds(expenseScores);

  const topIncome = incomeSorted.slice(0, maxSegmentsPerStack);
  const restIncome = incomeSorted.slice(maxSegmentsPerStack);
  const topExpense = expenseSorted.slice(0, maxSegmentsPerStack);
  const restExpense = expenseSorted.slice(maxSegmentsPerStack);

  const rows: Record<string, string | number>[] = [];

  for (const p of points) {
    const incomeMag = new Map<string, number>();
    const expenseMag = new Map<string, number>();
    for (const row of p.categoryBreakdown ?? []) {
      if (row.categoryType === "expense") {
        expenseMag.set(row.categoryId, row.amount);
      } else {
        incomeMag.set(row.categoryId, row.amount);
      }
    }

    const rec: Record<string, string | number> = {
      monthLabel: formatYmMonthShort(p.month),
      net: p.net,
      inflows: p.inflows,
      outflows: p.outflows,
    };

    for (const id of topIncome) {
      rec[incomeBarKey(id)] = incomeMag.get(id) ?? 0;
    }
    if (restIncome.length > 0) {
      let o = 0;
      for (const id of restIncome) {
        o += incomeMag.get(id) ?? 0;
      }
      rec[incomeBarKey(FLOW_OTHER_ID)] = o;
    }

    for (const id of topExpense) {
      rec[expenseBarKey(id)] = expenseMag.get(id) ?? 0;
    }
    if (restExpense.length > 0) {
      let o = 0;
      for (const id of restExpense) {
        o += expenseMag.get(id) ?? 0;
      }
      rec[expenseBarKey(FLOW_OTHER_ID)] = o;
    }

    rows.push(rec);
  }

  const incomeBars: FlowSeriesMeta[] = topIncome.map((id) => ({
    dataKey: incomeBarKey(id),
    displayName: incomeNames.get(id) ?? "Category",
    color: getSegmentColor(id),
  }));
  if (restIncome.length > 0) {
    incomeBars.push({
      dataKey: incomeBarKey(FLOW_OTHER_ID),
      displayName: "Other (inflows)",
      color: DEFAULT_CATEGORY_COLOR,
    });
  }

  const expenseBars: FlowSeriesMeta[] = topExpense.map((id) => ({
    dataKey: expenseBarKey(id),
    displayName: expenseNames.get(id) ?? "Category",
    color: getSegmentColor(id),
  }));
  if (restExpense.length > 0) {
    expenseBars.push({
      dataKey: expenseBarKey(FLOW_OTHER_ID),
      displayName: "Other (outflows)",
      color: DEFAULT_CATEGORY_COLOR,
    });
  }

  return { rows, incomeBars, expenseBars };
}

function sortBarsByTotalDescending(
  bars: FlowSeriesMeta[],
  rows: Record<string, string | number>[],
): FlowSeriesMeta[] {
  const totals = new Map<string, number>();
  for (const b of bars) {
    totals.set(b.dataKey, 0);
  }
  for (const row of rows) {
    for (const b of bars) {
      totals.set(
        b.dataKey,
        (totals.get(b.dataKey) ?? 0) + Number(row[b.dataKey] ?? 0),
      );
    }
  }
  return [...bars].sort(
    (a, b) => (totals.get(b.dataKey) ?? 0) - (totals.get(a.dataKey) ?? 0),
  );
}

export function ForecastChartPanel({
  data,
  isLoading,
  scenario,
  onScenarioChange,
  currency,
  categoryColorById,
  categoryParentById,
  categoryLabelById,
}: Props) {
  const [viewMode, setViewMode] = useState<"balance" | "flows">("balance");
  const chartAreaRef = useRef<HTMLDivElement | null>(null);
  const [canRenderFlowsChart, setCanRenderFlowsChart] = useState(false);

  const points = useMemo(
    () =>
      normalizePointsToParentBreakdown(
        data?.points ?? [],
        categoryParentById,
        categoryLabelById,
      ),
    [data?.points, categoryParentById, categoryLabelById],
  );

  const chartData = useMemo(
    () =>
      points.map((p) => ({
        ...p,
        monthLabel: formatYmMonthShort(p.month),
      })),
    [points],
  );

  const categoryBreakdownAvailable = useMemo(
    () => hasAnyCategoryBreakdown(points),
    [points],
  );

  const categoryBarModel = useMemo(() => {
    if (!categoryBreakdownAvailable || points.length === 0) return null;
    const getSegmentColor = (categoryId: string) =>
      resolveCategoryBarColor(categoryId, categoryColorById);
    const raw = buildCategoryBarChartModel(
      points,
      MAX_CATEGORY_BAR_SEGMENTS,
      getSegmentColor,
    );
    return {
      rows: raw.rows,
      incomeBars: sortBarsByTotalDescending(raw.incomeBars, raw.rows),
      expenseBars: sortBarsByTotalDescending(raw.expenseBars, raw.rows),
    };
  }, [categoryBreakdownAvailable, points, categoryColorById]);

  useEffect(() => {
    if (viewMode !== "flows") {
      setCanRenderFlowsChart(false);
      return;
    }
    const element = chartAreaRef.current;
    if (!element) {
      setCanRenderFlowsChart(false);
      return;
    }

    const updateSize = () => {
      const { width, height } = element.getBoundingClientRect();
      setCanRenderFlowsChart(width > 0 && height > 0);
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, [
    viewMode,
    isLoading,
    chartData.length,
    categoryBreakdownAvailable,
    categoryBarModel?.rows.length,
  ]);

  const formatAxisCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);

  const flowsTooltipStyle = {
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    fontSize: "12px",
  } as const;

  const showCategoryBars =
    viewMode === "flows" &&
    categoryBarModel !== null &&
    categoryBreakdownAvailable;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            Projected balance and flow breakdown
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            Visualizing expected cash trajectory based on current
            configurations.
          </p>
        </div>
        <select
          value={scenario}
          onChange={(e) =>
            onScenarioChange(e.target.value as "bad" | "mid" | "good")
          }
          className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-700 outline-none ring-emerald-500 focus:ring-2"
        >
          {PROJECTION_SCENARIO_SELECT_OPTIONS.map((tab) => (
            <option key={tab.value} value={tab.value}>
              {tab.label}
            </option>
          ))}
        </select>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <div className="mr-2 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
          <button
            type="button"
            onClick={() => setViewMode("balance")}
            className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
              viewMode === "balance"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600"
            }`}
          >
            Balance
          </button>
          <button
            type="button"
            onClick={() => setViewMode("flows")}
            className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
              viewMode === "flows"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600"
            }`}
          >
            Inflows & Outflows
          </button>
        </div>
        {viewMode === "balance" ? (
          <>
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1">
              <span className="h-2 w-2 rounded-full bg-blue-600" />
              Balance
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1">
              <span className="h-2 w-2 rounded-full bg-emerald-600" />
              Inflows
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-rose-100 bg-rose-50 px-2.5 py-1">
              <span className="h-2 w-2 rounded-full bg-rose-600" />
              Outflows
            </span>
          </>
        ) : showCategoryBars ? (
          <>
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1">
              <span className="h-2 w-2 rounded-full bg-green-500" />
              Inflows (stacked)
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-rose-100 bg-rose-50 px-2.5 py-1">
              <span className="h-2 w-2 rounded-full bg-red-500" />
              Outflows (stacked)
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1">
              <span className="h-2 w-2 rounded-full bg-blue-600" />
              Net Cashflow
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-slate-600">
              Top {MAX_CATEGORY_BAR_SEGMENTS} categories per bar + Other
            </span>
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1">
              <span className="h-2 w-2 rounded-full bg-green-500" />
              Inflows
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-rose-100 bg-rose-50 px-2.5 py-1">
              <span className="h-2 w-2 rounded-full bg-red-500" />
              Outflows
            </span>
          </>
        )}
      </div>
      {isLoading ? (
        <div className="h-72 animate-pulse rounded-xl bg-slate-100" />
      ) : !chartData.length ? (
        <p className="text-sm text-slate-500">No projection data yet.</p>
      ) : (
        <div
          ref={chartAreaRef}
          className="h-80 min-h-[320px] w-full min-w-0 rounded-xl border border-slate-200 bg-white p-2"
        >
          {viewMode === "balance" ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={formatAxisCurrency}
                  width={56}
                />
                <Tooltip
                  formatter={(value: number) =>
                    new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency,
                      maximumFractionDigits: 2,
                    }).format(value)
                  }
                />
                <ReferenceLine
                  y={0}
                  stroke="#94a3b8"
                  strokeWidth={1}
                  strokeDasharray="4 4"
                  ifOverflow="extendDomain"
                />
                <Line
                  type="monotone"
                  dataKey="closingBalance"
                  name="Balance"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : canRenderFlowsChart ? (
            <ResponsiveContainer
              width="100%"
              height="100%"
              minWidth={0}
              minHeight={320}
            >
              {showCategoryBars && categoryBarModel ? (
                <ComposedChart
                  data={categoryBarModel.rows}
                  margin={{ top: 8, right: 12, bottom: 4, left: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="monthLabel"
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                  />
                  <YAxis
                    tickFormatter={(v) => formatK(Number(v), currency)}
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                    width={56}
                  />
                  <ReferenceLine
                    y={0}
                    stroke="#94a3b8"
                    strokeDasharray="4 4"
                    ifOverflow="extendDomain"
                  />
                  <Tooltip
                    formatter={(value: number | string, name: string) => [
                      formatK(Number(value), currency),
                      name,
                    ]}
                    contentStyle={flowsTooltipStyle}
                  />
                  <Legend
                    wrapperStyle={{
                      maxHeight: 120,
                      overflowY: "auto",
                      fontSize: "11px",
                    }}
                    iconType="rect"
                  />
                  {categoryBarModel.incomeBars.map((s) => (
                    <Bar
                      key={s.dataKey}
                      stackId="inflow"
                      dataKey={s.dataKey}
                      name={s.displayName}
                      fill={s.color}
                      radius={[2, 2, 0, 0]}
                      barSize={14}
                    />
                  ))}
                  {categoryBarModel.expenseBars.map((s) => (
                    <Bar
                      key={s.dataKey}
                      stackId="outflow"
                      dataKey={s.dataKey}
                      name={s.displayName}
                      fill={s.color}
                      radius={[2, 2, 0, 0]}
                      barSize={14}
                    />
                  ))}
                  <Line
                    type="monotone"
                    dataKey="net"
                    name="Net Cashflow"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              ) : (
                <ComposedChart
                  data={chartData}
                  margin={{ top: 8, right: 12, bottom: 4, left: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="monthLabel"
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                  />
                  <YAxis
                    tickFormatter={(v) => formatK(Number(v), currency)}
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                    width={56}
                  />
                  <ReferenceLine
                    y={0}
                    stroke="#94a3b8"
                    strokeDasharray="4 4"
                    ifOverflow="extendDomain"
                  />
                  <Tooltip
                    formatter={(value: number | string, name: string) => [
                      formatK(Number(value), currency),
                      name,
                    ]}
                    contentStyle={flowsTooltipStyle}
                  />
                  <Legend wrapperStyle={{ fontSize: "11px" }} iconType="rect" />
                  <Bar
                    dataKey="inflows"
                    name="Inflows"
                    fill="#22c55e"
                    radius={[4, 4, 0, 0]}
                    barSize={14}
                  />
                  <Bar
                    dataKey="outflows"
                    name="Outflows"
                    fill="#ef4444"
                    radius={[4, 4, 0, 0]}
                    barSize={14}
                  />
                  <Line
                    type="monotone"
                    dataKey="net"
                    name="Net Cashflow"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              )}
            </ResponsiveContainer>
          ) : (
            <div className="h-full w-full animate-pulse rounded-lg bg-slate-100" />
          )}
        </div>
      )}
    </section>
  );
}
